import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IParty,
  PipesModule,
  TabsModule,
} from '@tablesready/common-ui';
import { AppIcon, AppIconColor, AppIconSize, FrontendIconsModule } from '@tablesready/frontend-icons';
import { BadgeModule } from '@tablesready/frontend-badge';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { IonicModule, MenuController } from '@ionic/angular';
import { combineLatest, Subscription, tap } from 'rxjs';
import { Party } from '@shared/party';
import { Location } from '@shared/location/models';
import { Features } from '@shared/account/models';
import { NotificationCenterService } from '@tablesready/frontend-notification-center';
import { ChatPartyService, FrontendChatPartyComponent } from '@tablesready/frontend-chat-party';

@Component({
  selector: 'lib-frontend-chat-party-modal',
  standalone: true,
  imports: [
    CommonModule,
    FrontendIconsModule,
    IonicModule,
    PipesModule,
    TabsModule,
    FrontendChatPartyComponent,
    BadgeModule,
  ],
  templateUrl: './frontend-chat-party-modal.component.html',
  styleUrl: './frontend-chat-party-modal.component.scss',
})
export class FrontendChatPartyModalComponent implements OnInit, OnDestroy {
  party?: Party | IParty;
  location?: Location;
  features?: Features;
  editPartyMethod?: (party: Party) => void;
  readOnly = false;
  canSendChat = true;
  onUpgradeNeeded?: () => void;
  showPartyInfo = false;
  isCompactLayout = false;

  service = inject(ChatPartyService);
  ncService = inject(NotificationCenterService);
  menuController = inject(MenuController);
  private readonly breakpointsService = inject(FrontendBreakpointsService);

  @ViewChild(FrontendChatPartyComponent) chatParty?: FrontendChatPartyComponent;

  private readonly subscription = new Subscription();

  closeSidebar: () => void = () => {
    void this.menuController.close('one-to-one-chat');
  };

  onMenuClose(): void {
    this.chatParty?.form?.reset();
  }

  ngOnInit(): void {
    const closeSub = this.service.closeChat$
      .pipe(tap(() => this.closeSidebar()))
      .subscribe();
    this.subscription.add(closeSub);

    const serviceSub = this.service.openChat$
      .pipe(tap((data) => {
        this.party = data.party;
        this.location = data.location;
        this.features = data.features;
        this.editPartyMethod = data.editPartyMethod;
        this.readOnly = data.readOnly ?? false;
        this.canSendChat = data.canSendChat ?? true;
        this.onUpgradeNeeded = data.onUpgradeNeeded;
        this.showPartyInfo = data.showPartyInfo ?? false;

        if (this.party._id) {
          this.ncService.markMessageAsRead(this.party._id).subscribe();
        }

        void this.menuController.open('one-to-one-chat');
      }))
      .subscribe();

    this.subscription.add(serviceSub);

    const layoutSub = combineLatest([
      this.breakpointsService.observeDesktopMode(),
      this.breakpointsService.observeShortHeight(),
    ]).subscribe(([isDesktop, isShortHeight]) => {
      this.isCompactLayout = !isDesktop || isShortHeight;
    });
    this.subscription.add(layoutSub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected readonly icon = AppIcon;
  protected readonly iconSize = AppIconSize;
  protected readonly iconColor = AppIconColor;
}
